import type { AiReport, AiReportCreate, AiReportSummary } from '@erp/shared';
import { useMutation, useQuery, useQueryClient, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query';

import { api } from '../../lib/api.js';

/** Poll cadence while a report is generating (the LLM takes 20-60 s). */
const POLL_MS = 2500;

export function useAiReports(): UseQueryResult<{ items: AiReportSummary[]; total: number }> {
  return useQuery({
    queryKey: ['ai-reports'],
    // One fetch, no paging: list rows are tiny and 500 covers years of reports.
    queryFn: async () => (await api().get<{ items: AiReportSummary[]; total: number }>('/ai-reports', { params: { limit: 500 } })).data,
    // Poll only while something is actually generating.
    refetchInterval: (q) => (q.state.data?.items.some((r) => r.status === 'running') ? POLL_MS : false),
  });
}

export function useAiReport(id: string | undefined): UseQueryResult<AiReport> {
  return useQuery({
    queryKey: ['ai-reports', id],
    enabled: !!id,
    queryFn: async () => (await api().get<AiReport>(`/ai-reports/${id}`)).data,
    refetchInterval: (q) => (q.state.data?.status === 'running' ? POLL_MS : false),
  });
}

export function useCreateAiReport(): UseMutationResult<AiReport, unknown, AiReportCreate> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: AiReportCreate) => (await api().post<AiReport>('/ai-reports', input)).data,
    onSuccess: (report) => {
      qc.setQueryData(['ai-reports', report.id], report);
      void qc.invalidateQueries({ queryKey: ['ai-reports'], exact: true });
    },
  });
}

export function useRegenerateAiReport(id: string): UseMutationResult<AiReport, unknown, void> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => (await api().post<AiReport>(`/ai-reports/${id}/regenerate`)).data,
    onSuccess: (report) => {
      qc.setQueryData(['ai-reports', id], report);
      void qc.invalidateQueries({ queryKey: ['ai-reports'], exact: true });
    },
  });
}

export function useDeleteAiReport(): UseMutationResult<void, unknown, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => { await api().delete(`/ai-reports/${id}`); },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['ai-reports'] }),
  });
}
